import type { Tool } from "@/lib/types";
import { slugify } from "@/lib/utils";

// ── Category definitions ─────────────────────────────────────────
// Each category has a one-line description, an icon (SVG path list),
// and its tools as [name, description] tuples.

interface CategoryDef {
  name: string;
  desc: string;
  /** SVG path `d` strings, stroked with the accent color by <ToolIcon>. */
  icon: string[];
  tools: Array<[name: string, desc: string]>;
}

export const CATEGORIES: CategoryDef[] = [
  {
    name: "Referral & Pre-Intake",
    desc: "Handle incoming referrals and triage new enquiries before the first session.",
    icon: ["M4 12h10", "M11 8l4 4-4 4", "M17 5v14"],
    tools: [
      ["Referral Triage Summary", "Turn a referral letter into a structured triage snapshot."],
      ["Pre-Intake Screener", "Draft a short pre-intake questionnaire for new enquiries."],
      ["Waitlist Letter", "Write a warm, clear waitlist confirmation for a client."],
    ],
  },
  {
    name: "Intake & Onboarding",
    desc: "Welcome new clients with intake forms, first-session structure, and clear consent.",
    icon: [
      "M14 3v4a1 1 0 0 0 1 1h4",
      "M6 3h9l5 5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z",
      "M9 13h6",
      "M9 17h4",
    ],
    tools: [
      ["Intake Form Generator", "Build an intake form tailored to your modality and setting."],
      ["First-Session Agenda", "Structure a first session: rapport, history, expectations."],
      ["Informed Consent Explainer", "Plain-language consent summary for clients to review."],
    ],
  },
  {
    name: "Assessment & Screening",
    desc: "Select, score, and interpret validated measures for the presenting concern.",
    icon: ["M4 19V9", "M10 19V5", "M16 19v-7", "M20 19V8"],
    tools: [
      ["Measure Selector", "Find validated measures for a presenting concern."],
      ["Score Interpreter", "Interpret screening scores with caveats and next steps."],
      ["Differential Checklist", "Organize observations against differential considerations."],
    ],
  },
  {
    name: "Case Conceptualization",
    desc: "Turn history and observations into a working formulation you can act on.",
    icon: [
      "M12 3a4 4 0 0 0-4 4c0 1.5.8 2.3 1 3 .3 1 .2 2-1 3",
      "M12 3a4 4 0 0 1 4 4c0 1.5-.8 2.3-1 3-.3 1-.2 2 1 3",
      "M8 16a4 4 0 0 0 8 0",
      "M12 20v1",
    ],
    tools: [
      ["Case Formulation Draft", "Draft a formulation from your session notes and history."],
      ["Maintenance Cycle Mapper", "Map triggers, responses, and maintaining factors."],
      ["Strengths & Resources Map", "Summarize client strengths, supports, and protective factors."],
    ],
  },
  {
    name: "Treatment Planning",
    desc: "Set measurable goals and match evidence-based interventions to the formulation.",
    icon: ["M9 11l3 3L22 4", "M21 12v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h11"],
    tools: [
      ["Goal & Objective Writer", "Translate broad goals into measurable objectives."],
      ["Intervention Matcher", "Match evidence-based interventions to the formulation."],
      ["Plan Review Assistant", "Review a treatment plan against progress to date."],
    ],
  },
  {
    name: "Session Support",
    desc: "Prep for sessions and pick the right in-session materials and homework.",
    icon: ["M12 8v8", "M8 12h8", "M4 4h16v12H5.2L4 18V4z"],
    tools: [
      ["Session Prep Brief", "One-page brief from last notes before the client arrives."],
      ["Worksheet Picker", "Choose an in-session worksheet for today’s focus."],
      ["Homework Generator", "Design a between-session task matched to this week’s work."],
    ],
  },
  {
    name: "Between-Session & Client-Facing",
    desc: "Client-friendly handouts, recaps, and coping tools for life between sessions.",
    icon: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"],
    tools: [
      ["Psychoeducation Handout", "Client-friendly handout on a topic, at a chosen reading level."],
      ["Client Summary Email", "Draft a supportive session recap email for the client."],
      ["Coping Card Builder", "Pocket-size coping statements from the client’s own words."],
    ],
  },
  {
    name: "Risk & Crisis",
    desc: "Build safety plans and structure risk assessments into a defensible record.",
    icon: ["M12 3l9 16H3z", "M12 10v4", "M12 17h.01"],
    tools: [
      ["Safety Plan Builder", "Collaborative safety plan with warning signs and contacts."],
      ["Risk Assessment Summary", "Structure a risk assessment into a defensible record."],
      ["Crisis Resources Sheet", "Localized crisis contacts sheet for a client to keep."],
    ],
  },
  {
    name: "Progress Review & Documentation",
    desc: "Write clean notes and summarize outcomes and progress over time.",
    icon: [
      "M14 3v4a1 1 0 0 0 1 1h4",
      "M6 3h9l5 5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z",
      "M8.5 13.5l2 2 4-4",
    ],
    tools: [
      ["Progress Note (SOAP)", "Turn shorthand into a clean SOAP or DAP note."],
      ["Outcome Trend Summary", "Narrative summary of outcome measures over time."],
      ["Supervision Case Brief", "Prepare a concise case brief for supervision."],
    ],
  },
  {
    name: "Discharge & Follow-Up",
    desc: "Close episodes of care well, with relapse prevention and caring follow-up.",
    icon: ["M9 21H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4", "M16 17l5-5-5-5", "M21 12H9"],
    tools: [
      ["Discharge Summary", "Comprehensive discharge summary from the case record."],
      ["Relapse Prevention Plan", "Early warning signs, skills, and supports for after therapy."],
      ["Follow-Up Check-In Letter", "A caring check-in letter for 1–3 months post-discharge."],
    ],
  },
  {
    name: "Reports & Third-Party Letters",
    desc: "Produce accommodation, insurance, and agency letters within your scope.",
    icon: ["M4 4h16v13H4z", "M4 8l8 5 8-5"],
    tools: [
      ["Accommodation Letter", "School or workplace accommodation letter, clinically grounded."],
      ["Insurance Report", "Medical-necessity report formatted for insurers."],
      ["Court / Agency Letter", "Careful third-party letter within scope-of-practice limits."],
    ],
  },
  {
    name: "Practice, Admin & Compliance",
    desc: "Draft policies, audit documentation, and keep the practice compliant.",
    icon: ["M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6z", "M9.5 12l1.8 1.8L15 10"],
    tools: [
      ["Policy Drafter", "Practice policies: cancellation, telehealth, communication."],
      ["Note Audit Checklist", "Audit your documentation against compliance standards."],
      ["Superbill Explainer", "Explain superbills and reimbursement to clients simply."],
    ],
  },
  {
    name: "Clinician Wellbeing & Development",
    desc: "Look after yourself and plan your own growth and consultation.",
    icon: [
      "M20.8 6.6a4.5 4.5 0 0 0-6.4 0l-.4.4-.4-.4a4.5 4.5 0 1 0-6.4 6.4l.4.4L14 20l6.4-6.6.4-.4a4.5 4.5 0 0 0 0-6.4z",
    ],
    tools: [
      ["Burnout Check-In", "A private, structured self-check with gentle suggestions."],
      ["Peer Consultation Prep", "Frame a case question for peer consultation."],
      ["CE Learning Plan", "Plan continuing education around your growth edges."],
    ],
  },
];

/** Tools gated behind the PRO plan. */
const PRO_TOOLS = new Set<string>([
  "Risk Assessment Summary",
  "Insurance Report",
  "Court / Agency Letter",
  "Case Formulation Draft",
  "Discharge Summary",
  "Outcome Trend Summary",
  "Accommodation Letter",
  "Note Audit Checklist",
]);

/** Fallback icon for any lookup miss. */
export const FALLBACK_ICON = CATEGORIES.find((c) => c.name === "Session Support")!.icon;

/** Category name → icon path list. */
export const CATEGORY_ICONS: Record<string, string[]> = Object.fromEntries(
  CATEGORIES.map((c) => [c.name, c.icon]),
);

/** Category name → one-line description. */
export const CATEGORY_DESCRIPTIONS: Record<string, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.name, c.desc]),
);

export const CATEGORY_NAMES: string[] = CATEGORIES.map((c) => c.name);

// ── Flattened, slugged tool list ─────────────────────────────────

export const ALL_TOOLS: Tool[] = CATEGORIES.flatMap((cat) =>
  cat.tools.map(([name, desc]) => ({
    name,
    slug: slugify(name),
    category: cat.name,
    desc,
    isPro: PRO_TOOLS.has(name),
  })),
);

const TOOL_BY_SLUG = new Map(ALL_TOOLS.map((t) => [t.slug, t]));
const TOOL_BY_NAME = new Map(ALL_TOOLS.map((t) => [t.name, t]));

export function getToolBySlug(slug: string): Tool | undefined {
  return TOOL_BY_SLUG.get(slug);
}

export function getToolByName(name: string): Tool | undefined {
  return TOOL_BY_NAME.get(name);
}

export function getToolIcon(category: string): string[] {
  return CATEGORY_ICONS[category] ?? FALLBACK_ICON;
}

/** Other tools in the same category (used by the "More from this category" rail). */
export function getRelatedTools(tool: Tool): Tool[] {
  return ALL_TOOLS.filter(
    (t) => t.category === tool.category && t.name !== tool.name,
  );
}
